import i18next from 'i18next';

import { ELanguage, II18nInitOptions, ILanguageResource, INamespaceResource } from './types';
import { detectLanguage, setStoredLanguage } from './utils';

let initialized = false;

/**
 * 默认 namespace
 *
 * 业务方调用 i18nTrans('xxx') / useLocale().t('xxx') 不传 ns 时，
 * 会到该 ns 下查找。
 */
export const DEFAULT_NS = 'common';

export function initI18n(options: II18nInitOptions): void {
  if (initialized) return;

  const {
    defaultLanguage = ELanguage.En,
    resources,
  } = options;

  const lng = detectLanguage(defaultLanguage);

  // 兼容两种资源结构：
  // 1) 嵌套 ns 结构（推荐）: { en: { common: {...}, apps: {...}, ... } }
  // 2) 扁平结构（legacy）  : { en: { 'key1': 'value1', 'key2': 'value2', ... } }
  //    扁平结构会被自动包裹到 DEFAULT_NS 下。
  const i18nResources: Record<string, Record<string, INamespaceResource>> = {};
  const namespaceSet = new Set<string>([DEFAULT_NS]);

  for (const lang of Object.keys(resources)) {
    const langResource: ILanguageResource = resources[lang];
    if (isNamespacedResource(langResource)) {
      const nested = langResource as Record<string, INamespaceResource>;
      i18nResources[lang] = nested;
      Object.keys(nested).forEach(ns => namespaceSet.add(ns));
    } else {
      // legacy 扁平资源：包裹到 DEFAULT_NS
      i18nResources[lang] = { [DEFAULT_NS]: langResource as INamespaceResource };
    }
  }

  i18next.init({
    lng,
    fallbackLng: ELanguage.En,
    defaultNS: DEFAULT_NS,
    fallbackNS: DEFAULT_NS,
    ns: Array.from(namespaceSet),
    nsSeparator: false,
    keySeparator: false,
    resources: i18nResources,
    interpolation: {
      escapeValue: false,
    },
  });

  initialized = true;
}

/**
 * 判断 lang 资源是嵌套 ns 结构还是扁平结构。
 *
 * 嵌套结构的 value 必须是对象（即 ns 内部的 key-value map），
 * 扁平结构的 value 是字符串。
 */
function isNamespacedResource(resource: ILanguageResource): boolean {
  for (const value of Object.values(resource)) {
    if (typeof value === 'string') return false;
    if (value !== null && typeof value === 'object') return true;
  }
  // 空对象按嵌套结构处理
  return true;
}

/**
 * 开发环境下检测缺失的 i18n key，在 console 报错
 */
export function warnMissingKey(key: string, ns: string = DEFAULT_NS): void {
  try {
    if (initialized && !i18next.exists(key, { ns })) {
      console.error(`[i18n] Missing translation key: "${key}" in ns "${ns}"`);
    }
  } catch {
    // ignore
  }
}

export type I18nOptions = Record<string, string | number>;

/**
 * 翻译函数。
 *
 * 查找顺序：ns → common（如果 ns !== common）
 *
 * @param key  翻译 key（通常即英文文案本身）
 * @param ns   命名空间。不传时使用 DEFAULT_NS（'common'）。
 *             也允许 `i18nTrans(key, options)` 这种 legacy 用法（第二个参数是 options 对象）。
 * @param options 插值参数
 *
 * @example
 *   i18nTrans('Save')                                  // -> common.Save
 *   i18nTrans('Apps')                                  // -> common.Apps
 *   i18nTrans('Edit app', 'apps')                      // -> 先查 apps，没有再查 common
 *   i18nTrans('Joined on {{date}}', { date: '...' })   // legacy: 传 options 当 ns
 *   i18nTrans('Joined on {{date}}', 'apps', { date })  // 推荐：显式 ns + options
 */
export function i18nTrans(key: string, ns?: string | I18nOptions, options?: I18nOptions): string {
  const { ns: realNs, options: realOptions } = normalizeArgs(ns, options);
  warnMissingKey(key, realNs);
  return i18next.t(key, { ns: realNs, ...(realOptions || {}) }) ?? key;
}

/**
 * 兼容两种调用形态：
 *   - i18nTrans(key, ns, options)
 *   - i18nTrans(key, options)
 */
export function normalizeArgs(
  arg2?: string | I18nOptions,
  arg3?: I18nOptions,
): { ns: string; options?: I18nOptions } {
  if (typeof arg2 === 'string') {
    return { ns: arg2, options: arg3 };
  }
  return { ns: DEFAULT_NS, options: arg2 };
}

export async function changeLanguage(lang: ELanguage): Promise<void> {
  setStoredLanguage(lang);
  await i18next.changeLanguage(lang);
}

export function getCurrentLanguage(): ELanguage {
  return (i18next.language as ELanguage) || ELanguage.En;
}
